import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ProgressCard } from 'components/apply-shared/progress-card';
import {
  FaCheck,
  FaClipboardCheck,
  FaPaperPlane,
  FaCreditCard,
} from 'react-icons/fa6';
import {
  ApplyAside,
  ApplyContainer,
  ApplyHeader,
  ApplyMain,
} from 'components/apply-shared/apply-form';
import { useGetSingleClaimQuery } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { claimTypeEnum, claimStatusEnum } from 'common/utils/enums';
import { doesClaimRequirePayment } from 'common/utils/misc';

// Import step components
import ResolvedClaim from 'components/claim-review/resolved-claim';
import Step1FinalizedOptions from 'components/claim-review/finalize-claim/step-1-finalized-options';
import Step2Payment from 'components/claim-review/finalize-claim/step-2-payment';
import Step3Confirmation from 'components/claim-review/finalize-claim/step-3-confirmation';

const ReviewClaim = () => {
  const { claimId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);

  // HOOKS
  const navigate = useNavigate();
  const { data: claimData, isLoading: getSingleClaimLoading } =
    useGetSingleClaimQuery(claimId, {
      refetchOnMountOrArgChange: true,
    });

  // Determine if claim is approved and requires payment
  const isApproved = claimData?.review?.dates?.moveOutStart || claimData?.review?.dates?.moveOutEnd;
  const requiresPayment = doesClaimRequirePayment(claimData);
  const isConfirmed = claimData?.status === claimStatusEnum.CONFIRMED;

  // HANDLERS
  const handleReturn = () => {
    navigate(`/dashboard/claims/${claimId}`);
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard/claims');
  };



  const handleReturnToClaim = () => {
    navigate(`/dashboard/claims/${claimId}`);
  };

  // Auto-navigate to final step if claim is already confirmed
  useEffect(() => {
    if (claimData && isConfirmed) {
      setCurrentStep(4);
    }
  }, [claimData, isConfirmed]);

  // Determine which step to show based on claim status and current step
  const getCurrentStepComponent = () => {
    // Step 1: Review Resolution (always shown first)
    // User reviews the claim resolution and selects move-out date if approved
    if (currentStep === 1) {
      return (
        <ResolvedClaim 
          claim={claimData} 
          claimId={claimId} 
          onComplete={() => {
            if (isApproved) {
              setCurrentStep(2);
            } else {
              handleReturn();
            }
          }}
        />
      );
    }

    // Step 2: Finalize Options (only for approved claims)
    // User confirms move-out date and proceeds to payment if required
    if (currentStep === 2 && isApproved) {
      return (
        <Step1FinalizedOptions
          claim={claimData}
          claimId={claimId}
          onProceedToPayment={() => setCurrentStep(3)}
          onFinalizeClaim={() => {
            if (requiresPayment) {
              setCurrentStep(3);
            } else {
              setCurrentStep(4);
            }
          }}
          onBackClick={() => setCurrentStep(1)}
        />
      );
    }

    // Step 3: Payment (only for approved claims that require payment)
    // User completes payment to finalize the claim
    if (currentStep === 3 && isApproved && requiresPayment) {
      return (
        <Step2Payment
          claim={claimData}
          claimId={claimId}
          onBackClick={() => setCurrentStep(2)}
          onPaymentComplete={() => setCurrentStep(4)}
        />
      );
    }

    // Step 4: Confirmation (only for approved claims)
    // User sees confirmation that claim has been finalized
    if (currentStep === 4 && isApproved) {
      return (
        <Step3Confirmation
          claim={claimData}
          claimId={claimId}
          onReturnToClaim={handleReturnToClaim}
        />
      );
    }

    // Fallback to step 1
    return (
      <ResolvedClaim 
        claim={claimData} 
        claimId={claimId} 
        onComplete={() => {
          if (isApproved) {
            setCurrentStep(2);
          } else {
            handleReturn();
          }
        }}
      />
    );
  };

  // Determine which progress cards to show
  const getProgressCards = () => {
    const cards = [];

    // Step 1: Review Resolution (always shown)
    cards.push(
      <ProgressCard
        key="step1"
        CardIcon={FaClipboardCheck}
        title='Review Resolution'
        step={1}
        isBlurred={false}
        isCompleted={currentStep > 1 || claimData?.submission?.confirmedDate}
      />
    );

    // Step 2: Finalize Options (only for approved claims)
    if (isApproved) {
      cards.push(
        <ProgressCard
          key="step2"
          CardIcon={FaPaperPlane}
          title='Finalize Options'
          step={2}
          isBlurred={currentStep < 2}
          isCompleted={currentStep > 2}
        />
      );
    }

    // Step 3: Payment (only for approved claims that require payment)
    if (isApproved && requiresPayment) {
      cards.push(
        <ProgressCard
          key="step3"
          CardIcon={FaCreditCard}
          title='Payment'
          step={3}
          isBlurred={currentStep < 3}
          isCompleted={currentStep > 3}
        />
      );
    }

    // Step 4: Confirmation (only for approved claims)
    if (isApproved) {
      cards.push(
        <ProgressCard
          key="step4"
          CardIcon={FaCheck}
          title='Confirmation'
          step={requiresPayment ? 4 : 3}
          isBlurred={currentStep < (requiresPayment ? 4 : 3)}
          isCompleted={false}
        />
      );
    }

    return cards;
  };

  return (
    <>
      <ApplyContainer>
        <ApplyHeader onClick={handleGoToDashboard} />

        <ApplyMain>
          {!isConfirmed && (
            <ApplyAside
              // Return to claim if user wants to exit the review
              save={handleReturn}
              saveText={claimId && 'Return to Claim'}
            >
              {getProgressCards()}
            </ApplyAside>
          )}

          {/* Main content area */}
          <section className='h-full w-full flex flex-col items-center border border-gray-200 divide-y divide-gray-200 rounded-lg mobile:border-none mobile:rounded-none mobile:divide-slate-200'>
            {getCurrentStepComponent()}
          </section>
        </ApplyMain>
      </ApplyContainer>
    </>
  );
};

export default ReviewClaim;
